// =============================================================================
// MODE CONVERSATIONNEL - Agent Conformité
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Bot,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  MessageSquare,
  ChevronRight,
  ArrowLeft,
  Clock,
  FileText,
  User,
  Wrench,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
} from "../../components/ui/card";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

function getHeaders(tenantId) {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...enteteTenant(tenantId),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// ---------------------------------------------------------------------------
// Message Bubble
// ---------------------------------------------------------------------------
function MessageBubble({ msg }) {
  const isAgent = msg.rôle === "agent";
  const typeConfig = {
    analysis_result: { icon: FileText, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
    success: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
    warning: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
    error: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
    info: { icon: Info, color: "text-[var(--oh-stat-cyan-text)]", bg: "bg-[var(--oh-stat-cyan-bg)] border-[var(--oh-stat-cyan-border)]" },
    answer: { icon: User, color: "text-[var(--oh-stat-purple-text)]", bg: "bg-[var(--oh-stat-purple-bg)] border-[var(--oh-stat-purple-border)]" },
  };

  const cfg = typeConfig[msg.type] || typeConfig.info;
  const Icon = cfg.icon;

  return (
    <div
      data-testid={`message-${msg.type}`}
      className={`flex ${isAgent ? "justify-start" : "justify-end"} mb-3`}
    >
      <div
        className={`max-w-[85%] rounded-xl px-4 py-3 border ${
          isAgent ? cfg.bg : "bg-violet-600/20 border-violet-500/30"
        }`}
      >
        <div className="flex items-start gap-2">
          <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isAgent ? cfg.color : "text-violet-400"}`} />
          <div>
            <p className="text-sm text-zinc-200 leading-relaxed">{msg.content}</p>
            {msg.timestamp && (
              <p className="text-[10px] text-zinc-500 mt-1">
                {new Date(msg.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question Card
// ---------------------------------------------------------------------------
function QuestionCard({ question, onAnswer, answering }) {
  const [inputValues, setInputValues] = useState({});
  const [selectedChoice, setSelectedChoice] = useState(null);

  const handleSubmit = () => {
    if (question.type === "choice" && selectedChoice !== null) {
      onAnswer(question.id, { value: question.choices[selectedChoice] });
    } else if (question.type === "input") {
      onAnswer(question.id, inputValues);
    }
  };

  return (
    <Card
      data-testid={`question-card-${question.id}`}
      className="border-amber-500/30 bg-amber-500/5 mx-4 mb-3"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-200 font-medium">{question.question}</p>
        </div>

        {question.type === "choice" && (
          <div className="space-y-2 mb-3">
            {question.choices.map((choice, idx) => (
              <button
                key={idx}
                data-testid={`choice-${idx}`}
                onClick={() => setSelectedChoice(idx)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedChoice === idx
                    ? "bg-[var(--oh-stat-amber-bg)] border border-[var(--oh-stat-amber-border)] text-[var(--oh-stat-amber-text)]"
                    : "bg-zinc-800/50 border border-zinc-700/50 text-zinc-300 hover:bg-zinc-700/50"
                }`}
              >
                {choice}
              </button>
            ))}
          </div>
        )}

        {question.type === "input" && (
          <div className="space-y-2 mb-3">
            {(question.fields || []).map((field) => (
              <div key={field}>
                <label className="text-xs text-zinc-400 mb-1 block capitalize">{field}</label>
                <Input
                  data-testid={`input-${field}`}
                  value={inputValues[field] || ""}
                  onChange={(e) => setInputValues(prev => ({ ...prev, [field]: e.target.value }))}
                  placeholder={field}
                  className="bg-zinc-900/50 border-zinc-700 text-white text-sm"
                />
              </div>
            ))}
          </div>
        )}

        <Button
          data-testid="submit-answer-btn"
          size="sm"
          onClick={handleSubmit}
          disabled={
            answering ||
            (question.type === "choice" && selectedChoice === null) ||
            (question.type === "input" && Object.values(inputValues).every(v => !v))
          }
          className="bg-amber-600 hover:bg-amber-700"
        >
          {answering ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Send className="w-3 h-3 mr-1" />
          )}
          Repondre
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Session List Item
// ---------------------------------------------------------------------------
function SessionItem({ session, onClick, active }) {
  const statusConfig = {
    pending: { label: "En attente", color: "bg-zinc-500/20 text-zinc-400" },
    analyzing: { label: "Analyse...", color: "bg-blue-500/20 text-blue-400" },
    questions: { label: "Questions", color: "bg-amber-500/20 text-amber-400" },
    resolved: { label: "Termine", color: "bg-emerald-500/20 text-emerald-400" },
    error: { label: "Erreur", color: "bg-red-500/20 text-red-400" },
  };
  const s = statusConfig[session.status] || statusConfig.pending;

  return (
    <button
      data-testid={`session-item-${session.session_id}`}
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all text-sm ${
        active
          ? "bg-violet-500/15 border border-violet-500/30"
          : "hover:bg-zinc-800/50 border border-transparent"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-zinc-200 font-medium truncate">
          {session.client_name || session.client_id?.slice(0, 8)}
        </span>
        <Badge className={`text-[10px] ${s.color}`}>{s.label}</Badge>
      </div>
      <p className="text-[10px] text-zinc-500 mt-1">
        {new Date(session.updated_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
      </p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main Conversation Panel
// ---------------------------------------------------------------------------
export default function ConversationPanel({ tenantId }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [startingNew, setStartingNew] = useState(false);

  // Client search for new session
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showNewSession, setShowNewSession] = useState(false);

  const messagesEndRef = useRef(null);
  const headers = getHeaders(tenantId);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sessionData?.messages]);

  // Load sessions list
  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/conformite/agent/conversations`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSessions(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error("Load sessions error:", e);
    }
  }, [tenantId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load session details
  const loadSessionData = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/conformite/agent/conversation/${sessionId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSessionData(data);
      }
    } catch (e) {
      console.error("Load session error:", e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Select a session
  const selectSession = (session) => {
    setActiveSession(session.session_id);
    setShowNewSession(false);
    loadSessionData(session.session_id);
  };

  // Search clients
  const searchClients = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await fetch(
        `${API}/api/metier/clients?search=${encodeURIComponent(q)}&limit=10`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.clients || data || []);
      }
    } catch (e) {
      console.error("Search error:", e);
    }
  };

  // Start new conversation
  const startConversation = async (clientId, clientName) => {
    setStartingNew(true);
    try {
      const res = await fetch(
        `${API}/api/conformite/agent/conversation/start/${clientId}`,
        { method: "POST", headers }
      );
      if (res.ok) {
        const data = await res.json();
        const sid = data.session_id;
        setActiveSession(sid);
        setSessionData(data);
        setShowNewSession(false);
        setSearchQuery("");
        setSearchResults([]);
        // Refresh sessions list
        loadSessions();
      }
    } catch (e) {
      console.error("Start conversation error:", e);
    } finally {
      setStartingNew(false);
    }
  };

  // Answer a question
  const handleAnswer = async (questionId, answer) => {
    if (!activeSession) return;
    setAnswering(true);
    try {
      const res = await fetch(
        `${API}/api/conformite/agent/conversation/${activeSession}/answer`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ question_id: questionId, answer }),
        }
      );
      if (res.ok) {
        // Reload session
        await loadSessionData(activeSession);
        loadSessions();
      }
    } catch (e) {
      console.error("Answer error:", e);
    } finally {
      setAnswering(false);
    }
  };

  return (
    <div data-testid="conversation-panel" className="flex h-[calc(100vh-120px)] rounded-xl overflow-hidden border border-zinc-800">
      {/* Sidebar - Sessions list */}
      <div className="w-72 bg-zinc-900/70 border-r border-zinc-800 flex flex-col flex-shrink-0">
        <div className="p-3 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-400" />
              Conversations
            </h3>
            <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{sessions.length}</Badge>
          </div>
          <Button
            data-testid="new-conversation-btn"
            size="sm"
            onClick={() => {
              setShowNewSession(true);
              setActiveSession(null);
              setSessionData(null);
            }}
            className="w-full bg-violet-600 hover:bg-violet-700 text-sm"
          >
            <Bot className="w-3 h-3 mr-1.5" />
            Nouvelle analyse
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <p className="text-xs text-zinc-500 text-center py-8">
              Aucune conversation
            </p>
          )}
          {sessions.map((s) => (
            <SessionItem
              key={s.session_id}
              session={s}
              active={activeSession === s.session_id}
              onClick={() => selectSession(s)}
            />
          ))}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col bg-zinc-950/50">
        {/* New session view */}
        {showNewSession && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-md space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center mx-auto mb-3">
                  <Bot className="w-8 h-8 text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Nouvelle analyse conversationnelle</h3>
                <p className="text-sm text-zinc-400 mt-1">
                  Selectionnez un client pour lancer l'analyse. L'agent posera des questions si des données sont ambigues.
                </p>
              </div>

              <Input
                data-testid="new-session-search"
                placeholder="Rechercher un client par nom..."
                value={searchQuery}
                onChange={(e) => searchClients(e.target.value)}
                className="bg-zinc-900/50 border-zinc-700 text-white"
              />

              {searchResults.length > 0 && (
                <div className="max-h-60 overflow-y-auto space-y-1 border border-zinc-700/50 rounded-lg p-2">
                  {searchResults.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`new-session-client-${c.id}`}
                      onClick={() => startConversation(c.id, `${c.nom} ${c.prenom || ""}`)}
                      disabled={startingNew}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm hover:bg-violet-500/10 transition text-zinc-300 group"
                    >
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-zinc-500 group-hover:text-violet-400" />
                        <span className="font-medium">{c.nom} {c.prenom}</span>
                        {c.email && <span className="text-xs text-zinc-500">{c.email}</span>}
                      </div>
                      {startingNew ? (
                        <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[var(--oh-text-secondary)] group-hover:text-violet-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active session view */}
        {activeSession && !showNewSession && (
          <>
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/30">
              <div className="flex items-center gap-3">
                <Bot className="w-5 h-5 text-violet-400" />
                <div>
                  <h3 className="text-sm font-semibold text-white">
                    Session {activeSession?.slice(0, 8)}
                  </h3>
                  <p className="text-[10px] text-zinc-500">
                    {sessionData?.status === "resolved" ? "Analyse terminee" :
                     sessionData?.status === "questions" ? "En attente de reponses" :
                     sessionData?.status === "analyzing" ? "Analyse en cours..." : ""}
                  </p>
                </div>
              </div>
              {sessionData && (
                <Badge className={
                  sessionData.status === "resolved" ? "bg-emerald-500/20 text-emerald-400" :
                  sessionData.status === "questions" ? "bg-amber-500/20 text-amber-400" :
                  sessionData.status === "error" ? "bg-red-500/20 text-red-400" :
                  "bg-blue-500/20 text-blue-400"
                }>
                  {sessionData.status === "resolved" ? "Termine" :
                   sessionData.status === "questions" ? "Questions en attente" :
                   sessionData.status === "error" ? "Erreur" : "Analyse..."}
                </Badge>
              )}
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
                </div>
              )}

              {!loading && sessionData?.messages?.map((msg, idx) => (
                <MessageBubble key={idx} msg={msg} />
              ))}

              {/* Pending questions */}
              {!loading && sessionData?.pending_questions?.map((q) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  onAnswer={handleAnswer}
                  answering={answering}
                />
              ))}

              {/* Session resolved */}
              {!loading && sessionData?.status === "resolved" && (
                <div className="text-center py-6">
                  <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-emerald-300 font-medium">Analyse terminee</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Toutes les données ont ete extraites et validées.
                  </p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </>
        )}

        {/* Empty state */}
        {!activeSession && !showNewSession && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-[var(--oh-text-muted)] mx-auto mb-3" />
              <p className="text-sm text-zinc-500">
                Selectionnez une conversation ou lancez une nouvelle analyse
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
